import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Loader2, Copy, ArrowLeftRight, ArrowUpRight, ArrowDownLeft, Receipt } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface SearchResult {
  type: 'transaction' | 'withdrawal' | 'deposit' | 'internal_transfer';
  id: string;
  transaction_number?: string | null;
  tx_hash?: string | null;
  crypto_symbol: string;
  amount: number;
  status: string;
  created_at: string;
  user_id?: string;
  sender_id?: string;
  recipient_id?: string;
  sender_name?: string | null;
  recipient_name?: string | null;
  network?: string;
  wallet_address?: string;
}

const AdminTransactionSearch = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const { toast } = useToast();

  const handleSearch = async () => {
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setSearched(true);
    const allResults: SearchResult[] = [];

    try {
      // Search transactions by transaction_number
      const { data: txByNum } = await supabase
        .from("transactions")
        .select("*")
        .ilike("transaction_number", `%${q}%`);
      txByNum?.forEach(t => allResults.push({
        type: 'transaction', id: t.id, transaction_number: t.transaction_number,
        crypto_symbol: t.crypto_symbol, amount: t.amount, status: t.status,
        created_at: t.created_at, user_id: t.user_id, network: t.network,
        wallet_address: t.wallet_address,
      }));

      // Search withdrawals by transaction_number or tx_hash
      const { data: wdByNum } = await supabase
        .from("withdrawal_requests")
        .select("*")
        .or(`transaction_number.ilike.%${q}%,tx_hash.ilike.%${q}%`);
      wdByNum?.forEach(w => allResults.push({
        type: 'withdrawal', id: w.id, transaction_number: w.transaction_number,
        tx_hash: w.tx_hash, crypto_symbol: w.crypto_symbol, amount: w.amount,
        status: w.status, created_at: w.created_at, user_id: w.user_id,
        network: w.network, wallet_address: w.wallet_address,
      }));

      // Search deposits by tx_hash or DP- transaction_number
      const { data: depByHash } = await supabase
        .from("deposit_requests")
        .select("*")
        .or(`tx_hash.ilike.%${q}%,transaction_number.ilike.%${q}%`);
      depByHash?.forEach(d => allResults.push({
        type: 'deposit', id: d.id, tx_hash: d.tx_hash, transaction_number: d.transaction_number,
        crypto_symbol: d.crypto_symbol, amount: d.amount || 0,
        status: d.status, created_at: d.created_at, user_id: d.user_id,
        network: d.network,
      }));

      // Search internal transfers by transaction_number
      const { data: itByNum } = await supabase
        .from("internal_transfers")
        .select("*")
        .ilike("transaction_number", `%${q}%`);
      itByNum?.forEach(it => allResults.push({
        type: 'internal_transfer', id: it.id, transaction_number: it.transaction_number,
        crypto_symbol: it.crypto_symbol, amount: it.amount, status: 'completed',
        created_at: it.created_at, sender_id: it.sender_id, recipient_id: it.recipient_id,
        sender_name: it.sender_name, recipient_name: it.recipient_name,
        network: 'داخلي',
      }));

      // Sort by date descending
      allResults.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setResults(allResults);
    } catch (err) {
      console.error(err);
      toast({ title: "خطأ في البحث", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const typeConfig: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
    transaction: { label: 'شراء', icon: <Receipt className="w-4 h-4" />, color: 'bg-primary/20 text-primary' },
    withdrawal: { label: 'سحب', icon: <ArrowUpRight className="w-4 h-4" />, color: 'bg-crypto-red/20 text-crypto-red' },
    deposit: { label: 'إيداع', icon: <ArrowDownLeft className="w-4 h-4" />, color: 'bg-crypto-green/20 text-crypto-green' },
    internal_transfer: { label: 'تحويل داخلي', icon: <ArrowLeftRight className="w-4 h-4" />, color: 'bg-crypto-gold/20 text-crypto-gold' },
  };

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      completed: 'bg-crypto-green/20 text-crypto-green',
      approved: 'bg-crypto-green/20 text-crypto-green',
      pending: 'bg-crypto-gold/20 text-crypto-gold',
      rejected: 'bg-crypto-red/20 text-crypto-red',
      failed: 'bg-crypto-red/20 text-crypto-red',
    };
    return map[status] || 'bg-muted text-muted-foreground';
  };

  const copyText = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "تم النسخ" });
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Search className="w-5 h-5 text-primary" />
          بحث عن حركة مالية
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-6">
          <Input
            placeholder="ابحث برقم العملية (TX-, WD-, IT-) أو TxID..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            className="bg-input border-border text-foreground"
            dir="ltr"
          />
          <Button onClick={handleSearch} disabled={loading || !query.trim()}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>

        {searched && results.length === 0 && !loading && (
          <div className="text-center py-10 text-muted-foreground">
            <Search className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>لم يتم العثور على نتائج لـ "{query}"</p>
          </div>
        )}

        {results.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground mb-3">تم العثور على {results.length} نتيجة</p>
            {results.map(r => {
              const cfg = typeConfig[r.type];
              return (
                <div key={`${r.type}-${r.id}`} className="p-4 rounded-xl bg-secondary/50 border border-border/50 hover:bg-secondary/80 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                        {cfg.icon} {cfg.label}
                      </span>
                      {(r.transaction_number || r.tx_hash) && (
                        <button
                          onClick={() => copyText(r.transaction_number || r.tx_hash || '')}
                          className="flex items-center gap-1 text-xs font-mono text-primary bg-primary/10 px-2 py-0.5 rounded hover:bg-primary/20 transition-colors"
                          dir="ltr"
                        >
                          {r.transaction_number || (r.tx_hash ? r.tx_hash.slice(0, 12) + '...' : '')}
                          <Copy className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <Badge className={`${statusBadge(r.status)} border-0`}>{r.status}</Badge>
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">المبلغ: </span>
                      <span className="text-foreground font-semibold">{r.amount} {r.crypto_symbol}</span>
                    </div>
                    {r.network && (
                      <div>
                        <span className="text-muted-foreground">الشبكة: </span>
                        <span className="text-foreground">{r.network}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-muted-foreground">التاريخ: </span>
                      <span className="text-foreground">{format(new Date(r.created_at), "yyyy/MM/dd HH:mm")}</span>
                    </div>
                  </div>

                  {r.type === 'internal_transfer' && (
                    <div className="flex gap-4 text-sm mt-2">
                      <div>
                        <span className="text-muted-foreground">المرسل: </span>
                        <span className="text-foreground">{r.sender_name || 'غير معروف'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">المستقبل: </span>
                        <span className="text-foreground">{r.recipient_name || 'غير معروف'}</span>
                      </div>
                    </div>
                  )}

                  {r.wallet_address && (
                    <div className="text-xs mt-2 text-muted-foreground">
                      <span>العنوان: </span>
                      <button onClick={() => copyText(r.wallet_address!)} className="font-mono text-primary hover:underline" dir="ltr">
                        {r.wallet_address.slice(0, 16)}...{r.wallet_address.slice(-8)}
                        <Copy className="w-3 h-3 inline mr-1" />
                      </button>
                    </div>
                  )}

                  {r.tx_hash && r.transaction_number && (
                    <div className="text-xs mt-1 text-muted-foreground">
                      <span>TxID: </span>
                      <button onClick={() => copyText(r.tx_hash!)} className="font-mono text-primary hover:underline" dir="ltr">
                        {r.tx_hash.slice(0, 16)}...{r.tx_hash.slice(-8)}
                        <Copy className="w-3 h-3 inline mr-1" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminTransactionSearch;
